import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { designJobService } from '@/services/designJobService'

interface TimerState {
  status: 'running' | 'paused' | 'stopped'
  current_session_id: string | null
  started_at: string | null
  paused_at: string | null
  total_paused_seconds: number
}

interface ActiveTimer {
  jobUuid: string
  jobTitle: string
  timer: TimerState
  total_tracked_seconds: number
}

export const useDesignJobTimerStore = defineStore('designJobTimer', () => {
  // State - only one active timer per user
  const activeTimer = ref<ActiveTimer | null>(null)
  const isLoading = ref(false)

  // Computed
  const hasActiveTimer = computed(() => {
    return activeTimer.value !== null && 
           (activeTimer.value.timer.status === 'running' || 
            activeTimer.value.timer.status === 'paused')
  })

  const tickCounter = ref(0) // Force reactivity by incrementing on each tick

  const currentSessionSeconds = computed(() => {
    if (!activeTimer.value || !activeTimer.value.timer.started_at) {
      return 0
    }
    
    const timer = activeTimer.value.timer
    const _ = tickCounter.value // Access tickCounter to make it a dependency
    
    if (timer.status === 'running' && timer.started_at) {
      const now = new Date()
      const started = new Date(timer.started_at)
      const elapsed = Math.floor((now.getTime() - started.getTime()) / 1000)
      return Math.max(0, elapsed - timer.total_paused_seconds)
    } else if (timer.status === 'paused' && timer.paused_at && timer.started_at) {
      const paused = new Date(timer.paused_at)
      const started = new Date(timer.started_at)
      const elapsed = Math.floor((paused.getTime() - started.getTime()) / 1000)
      return Math.max(0, elapsed - timer.total_paused_seconds)
    }
    
    return 0
  })

  // Actions
  const startTimer = async (jobUuid: string, jobTitle: string, totalTrackedSeconds: number = 0) => {
    if (isLoading.value) return // Prevent concurrent calls
    
    isLoading.value = true
    try {
      // If there's already an active timer for a DIFFERENT job, stop it first
      // But if it's for the SAME job, check backend state first
      if (activeTimer.value) {
        if (activeTimer.value.jobUuid !== jobUuid) {
          // Different job - stop it
          await stopTimer()
        } else {
          // Same job - check if backend actually has a running timer
          const timerResponse = await designJobService.getTimerTotal(jobUuid)
          if (timerResponse.success) {
            if (!timerResponse.current_session || timerResponse.current_session.ended_at) {
              // Backend says no active session or it's ended - clear frontend state
              activeTimer.value = null
            } else {
              // Backend has an active session - sync to backend state
              if (timerResponse.current_session.uuid !== activeTimer.value.timer.current_session_id) {
                // Backend has a different session - sync to backend state
                activeTimer.value = {
                  jobUuid,
                  jobTitle,
                  timer: {
                    status: 'running',
                    current_session_id: timerResponse.current_session.uuid,
                    started_at: timerResponse.current_session.started_at,
                    paused_at: null,
                    total_paused_seconds: 0
                  },
                  total_tracked_seconds: timerResponse.total_seconds || 0
                }
              } else {
                // Session matches - update total tracked seconds
                activeTimer.value.total_tracked_seconds = timerResponse.total_seconds || 0
              }
              // Timer is already running - don't start a new one
              return
            }
          }
        }
      }
      
      // Call API to start timer
      const response = await designJobService.startTimer(jobUuid)
      
      if (response.success && response.time_entry) {
    activeTimer.value = {
      jobUuid,
      jobTitle,
      timer: {
        status: 'running',
            current_session_id: response.time_entry.uuid,
            started_at: response.time_entry.started_at,
        paused_at: null,
        total_paused_seconds: 0
      },
      total_tracked_seconds: totalTrackedSeconds
        }
      } else {
        throw new Error(response.message || 'Failed to start timer')
      }
    } catch (error: any) {
      // If backend says there's already a running timer, try to sync state
      if (error?.message?.includes('already have a running timer')) {
        try {
          // Check if there's an active timer for this specific job
          const timerResponse = await designJobService.getTimerTotal(jobUuid)
          if (timerResponse.success && timerResponse.current_session && !timerResponse.current_session.ended_at) {
            // Backend has an active session for this job - load it into state
            activeTimer.value = {
              jobUuid,
              jobTitle,
              timer: {
                status: 'running',
                current_session_id: timerResponse.current_session.uuid,
                started_at: timerResponse.current_session.started_at,
                paused_at: null,
                total_paused_seconds: 0
              },
              total_tracked_seconds: timerResponse.total_seconds || 0
            }
            // Don't throw error - we've synced the state
            return
          } else {
            // There's an active timer for a different job - we can't handle this automatically
            throw new Error('You have a running timer on another job. Please stop it first.')
          }
        } catch (syncError: any) {
          // If sync fails, throw original error
          throw error
        }
      } else {
        // Other errors - just throw them
        throw error
      }
    } finally {
      isLoading.value = false
    }
  }
  
  // Tick the counter to force reactivity in computed properties
  const tick = () => {
    tickCounter.value++
  }

  const pauseTimer = async () => {
    if (!activeTimer.value || activeTimer.value.timer.status !== 'running' || isLoading.value) return
    
    isLoading.value = true
    try {
      // Pause works exactly like stop, but preserves state to keep widget visible
      const jobUuid = activeTimer.value.jobUuid
      const jobTitle = activeTimer.value.jobTitle
      const sessionId = activeTimer.value.timer.current_session_id
      
      if (!sessionId) {
        // No session to stop
        activeTimer.value.timer.status = 'paused'
        return
      }
      
      // Stop the backend timer but preserve frontend state
      const response = await designJobService.stopTimer(jobUuid, sessionId)
      
      // Get updated total from backend
      const timerResponse = await designJobService.getTimerTotal(jobUuid)
      if (timerResponse.success && activeTimer.value) {
        // Update total and mark as paused (keep widget visible)
        activeTimer.value.total_tracked_seconds = timerResponse.total_seconds || 0
        activeTimer.value.timer.status = 'paused'
        activeTimer.value.timer.current_session_id = null
        activeTimer.value.timer.started_at = null
        activeTimer.value.timer.paused_at = null
        activeTimer.value.timer.total_paused_seconds = 0
      }
    } finally {
      isLoading.value = false
    }
  }

  const resumeTimer = async () => {
    if (!activeTimer.value || activeTimer.value.timer.status !== 'paused' || isLoading.value) return
    
    isLoading.value = true
    try {
      // Resume works exactly like start - just start a new timer session
      const jobUuid = activeTimer.value.jobUuid
      const jobTitle = activeTimer.value.jobTitle
      const currentTotal = activeTimer.value.total_tracked_seconds
      
      // Preserve the timer state during start (don't clear it)
      // Call API to start timer
      const response = await designJobService.startTimer(jobUuid)
      
      if (response.success && response.time_entry && activeTimer.value) {
        // Update the existing timer state instead of clearing it
        activeTimer.value.timer.status = 'running'
        activeTimer.value.timer.current_session_id = response.time_entry.uuid
        activeTimer.value.timer.started_at = response.time_entry.started_at
        activeTimer.value.timer.paused_at = null
        activeTimer.value.timer.total_paused_seconds = 0
        // Keep the existing total_tracked_seconds (it will be updated as the session runs)
      } else if (response.success && response.time_entry) {
        // If activeTimer was cleared somehow, recreate it
        activeTimer.value = {
          jobUuid,
          jobTitle,
          timer: {
            status: 'running',
            current_session_id: response.time_entry.uuid,
            started_at: response.time_entry.started_at,
            paused_at: null,
            total_paused_seconds: 0
          },
          total_tracked_seconds: currentTotal
        }
      } else {
        throw new Error(response.message || 'Failed to start timer')
      }
    } finally {
      isLoading.value = false
    }
  }

  const stopTimer = async (reason: 'manual' | 'auto_submit' = 'manual', entryUuid?: string, preserveState: boolean = false) => {
    if (!activeTimer.value) return
    
    // Don't set loading for preserveState=true (pause) as pauseTimer manages its own loading
    if (!preserveState && isLoading.value) return
    
    if (!preserveState) {
      isLoading.value = true
    }
    
    try {
      const timerEntryUuid = entryUuid || activeTimer.value.timer.current_session_id
      if (!timerEntryUuid) {
        // No active session to stop
        if (!preserveState) {
          activeTimer.value = null
        }
        return
      }
      
      // For real stops (not pause), check backend state first to avoid unnecessary API calls
      if (!preserveState) {
        const timerResponse = await designJobService.getTimerTotal(activeTimer.value.jobUuid)
        if (timerResponse.success) {
          // If backend says no active session or it's already ended, just update state
          if (!timerResponse.current_session || timerResponse.current_session.ended_at) {
            // Timer is already stopped - just update total and clear state
            if (activeTimer.value) {
              activeTimer.value.total_tracked_seconds = timerResponse.total_seconds || 0
            }
            activeTimer.value = null
            return
          }
          // If session UUID doesn't match, sync to backend state
          if (timerResponse.current_session.uuid !== timerEntryUuid) {
            // Backend has a different session - sync state
            activeTimer.value = {
              jobUuid: activeTimer.value.jobUuid,
              jobTitle: activeTimer.value.jobTitle,
              timer: {
                status: timerResponse.current_session.ended_at ? 'stopped' : 'running',
                current_session_id: timerResponse.current_session.uuid,
                started_at: timerResponse.current_session.started_at,
                paused_at: null,
                total_paused_seconds: 0
              },
              total_tracked_seconds: timerResponse.total_seconds || 0
            }
            // Try to stop the correct session
            const correctEntryUuid = timerResponse.current_session.uuid
            const response = await designJobService.stopTimer(activeTimer.value.jobUuid, correctEntryUuid)
            if (response.success) {
              const totalResponse = await designJobService.getTimerTotal(activeTimer.value.jobUuid)
              if (totalResponse.success && activeTimer.value) {
                activeTimer.value.total_tracked_seconds = totalResponse.total_seconds || 0
              }
            }
            activeTimer.value = null
            return
          }
        }
      }
      
      // Call API to stop timer
      const response = await designJobService.stopTimer(activeTimer.value.jobUuid, timerEntryUuid)
      
      // If backend says timer is already stopped, treat it as success
      const isAlreadyStopped = !response.success && (
        response.message?.includes('already stopped') || 
        response.error?.includes('already stopped')
      )
      
      if (response.success || isAlreadyStopped) {
        // Get updated total from API
        const totalResponse = await designJobService.getTimerTotal(activeTimer.value.jobUuid)
        if (totalResponse.success) {
          if (preserveState && activeTimer.value) {
            // Update total but preserve timer state (for pause)
            // Keep current_session_id and started_at for display purposes
            activeTimer.value.total_tracked_seconds = totalResponse.total_seconds
          } else if (activeTimer.value) {
            // Update total before clearing (for stop)
            activeTimer.value.total_tracked_seconds = totalResponse.total_seconds
          }
        }
        
        // Clear active timer only if not preserving state (for pause)
        if (!preserveState) {
          activeTimer.value = null
        }
      } else {
        // Other errors - log but still try to update state
        console.error('Error stopping timer:', response.message || response.error)
        // Still clear local state even if API call fails (unless preserving for pause)
        if (!preserveState) {
          activeTimer.value = null
        }
      }
    } catch (error: any) {
      // Network or other unexpected errors
      console.error('Error stopping timer:', error)
      // Still clear local state even if API call fails (unless preserving for pause)
      if (!preserveState) {
        activeTimer.value = null
      }
    } finally {
      if (!preserveState) {
        isLoading.value = false
      }
    }
  }

  const loadTimerState = async (jobUuid: string, jobTitle: string) => {
    try {
      const response = await designJobService.getTimerTotal(jobUuid)
      
      if (response.success) {
        // Store only completed sessions total (current session will be added in computed property)
        const baseTotal = response.total_seconds || 0
        
        // Check if store has paused timer for this job
        const hasPausedTimer = activeTimer.value && 
                               activeTimer.value.jobUuid === jobUuid && 
                               activeTimer.value.timer.status === 'paused'
        
        if (response.current_session) {
          // Backend has an active session
          if (hasPausedTimer && activeTimer.value && activeTimer.value.timer.current_session_id === response.current_session.uuid) {
            // Same session that was paused - preserve paused state
            activeTimer.value.total_tracked_seconds = baseTotal
            // Keep paused state, don't overwrite
          } else {
            // Different session or no paused state - load from backend
            activeTimer.value = {
              jobUuid,
              jobTitle,
              timer: {
                status: response.current_session.ended_at ? 'stopped' : 'running',
                current_session_id: response.current_session.uuid,
                started_at: response.current_session.started_at,
                paused_at: null,
                total_paused_seconds: 0
              },
              total_tracked_seconds: baseTotal
            }
          }
        } else {
          // Backend has no active session
          if (hasPausedTimer && activeTimer.value) {
            // Store has paused state - preserve it and update total
            activeTimer.value.total_tracked_seconds = baseTotal
            // Keep paused state intact
          } else {
            // No paused state - update total if timer exists for this job
            if (activeTimer.value && activeTimer.value.jobUuid === jobUuid) {
              activeTimer.value.total_tracked_seconds = baseTotal
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading timer state:', error)
    }
  }

  return {
    // State
    activeTimer,
    isLoading,
    
    // Computed
    hasActiveTimer,
    currentSessionSeconds,
    
    // Actions
    startTimer,
    pauseTimer,
    resumeTimer,
    stopTimer,
    loadTimerState,
    tick
  }
})

